<template>
  <div id="app">
    <Loading :loading="isLoading" />
    <v-col class="pa-0">
      <v-form ref="form" lazy-validation>
        <v-card class="pt-2">
          <v-col class="pa-0">
            <v-select
              class="ma-4"
              label="記事タイプ"
              :readonly="!editableComponent"
              return-object
              :value="articleType"
              @input="updateArticleType($event)"
              item-text="name"
              item-value="id"
              :items="articleTypes"
            />
          </v-col>
          <v-col class="pa-0 ma-4" v-if="isWellKnown">
            <v-row>
              <v-btn :disabled="!editableComponent" @click="selectCurator"
                >選択</v-btn
              >
              <v-text-field
                :readonly="true"
                :value="curator ? curator.name : ''"
                label="著名人"
              ></v-text-field>
              <SelectCurator ref="selectCurator" />
            </v-row>
          </v-col>
          <v-col class="pa-0">
            <v-text-field
              class="ma-4"
              counter
              :readonly="!editableComponent"
              :value="title"
              @input="updateArticle({ title: $event })"
              label="記事タイトル"
            ></v-text-field>
            <v-alert type="warning" outlined :value="title.length > 36"
              >タイトルが長すぎます</v-alert
            >
          </v-col>
          <v-divider v-if="role === 'editor' || role === 'administrator'" />
          <v-col
            class="pa-0"
            v-if="role === 'editor' || role === 'administrator'"
          >
            <v-col class="pa-0">
              <v-text-field
                class="ma-4"
                label="RSS配信用タイトル"
                :readonly="!editableComponent"
                :value="rssTitle"
                @input="updateArticle({ rssTitle: $event })"
              ></v-text-field>
            </v-col>
            <v-col class="pa-0 ma-4">
              <v-checkbox
                class="mx-2"
                color="blue"
                :readonly="!editableComponent"
                :input-value="showRss"
                @change="updateArticle({ showRss: !showRss })"
                :label="`RSS配信する`"
              />
            </v-col>
          </v-col>
          <v-divider />
          <v-col class="pa-0">
            <v-textarea
              class="ma-4"
              :readonly="!editableComponent"
              counter
              :value="overview"
              @input="updateArticle({ overview: $event })"
              label="概要"
            ></v-textarea>
          </v-col>
          <v-divider />
          <v-row no-gutters>
            <v-col>
              <v-card-title>アイキャッチ画像</v-card-title>
              <img
                class="eyecatch-image"
                :src="eyecatch.dataURL || eyecatch.url"
                v-if="eyecatch.url"
              />
              <p v-if="eyecatch.filename">
                ファイル名: {{ eyecatch.filename }}
              </p>
              <v-col class="pa-0 ma-4">
                <ImageCrop
                  :type="`eyecatch`"
                  :maxWidth="800"
                  :aspectRatio="16 / 9"
                  :init-quality="1"
                  :readonly="!editableComponent"
                  ref="eyecatchCrop"
                />
                <v-btn :disabled="!editableComponent" @click="showEyecatchCrop"
                  >アイキャッチ画像を選択</v-btn
                >
              </v-col>
            </v-col>
            <v-col>
              <v-card-title>アイコン画像</v-card-title>
              <img
                class="thumbnail-image"
                :src="thumbnail.dataURL || thumbnail.url"
                v-if="thumbnail.url"
              />
              <p v-if="thumbnail.filename">
                ファイル名: {{ thumbnail.filename }}
              </p>
              <v-col class="pa-0 ma-4">
                <ImageCrop
                  :type="`thumbnail`"
                  :maxWidth="440"
                  :aspectRatio="16 / 9"
                  :init-quality="1"
                  :readonly="!editableComponent"
                  ref="thumbnailCrop"
                />
                <v-btn :disabled="!editableComponent" @click="showThumbnailCrop"
                  >アイコン画像を選択</v-btn
                >
              </v-col>
            </v-col>
          </v-row>
          <v-divider v-if="role === 'administrator' || role === 'editor'" />
          <v-col class="pa-0">
            <v-row
              class="ma-4"
              v-if="role === 'administrator' || role === 'editor'"
            >
              <v-btn :disabled="!editableComponent" @click="selectWriter"
                >選択</v-btn
              >
              <v-text-field
                :readonly="true"
                :value="writer ? writer.name : ''"
                label="ライター"
              ></v-text-field>
              <v-checkbox
                color="blue"
                :readonly="!editableComponent"
                :label="`ライターを表示`"
                :input-value="writerDisplayEnabled"
                @change="updateArticle({ writerDisplayEnabled: $event })"
              />
              <SelectAdminUser
                :roleName="`ライター`"
                :role="`writer`"
                ref="selectWriter"
              />
            </v-row>
            <v-row
              class="ma-4"
              v-if="role === 'administrator' || role === 'editor'"
            >
              <v-btn :disabled="!editableComponent" @click="selectEditor"
                >選択</v-btn
              >
              <v-text-field
                :readonly="true"
                :value="editor ? editor.name : ''"
                label="編集者"
              ></v-text-field>
              <SelectAdminUser
                :roleName="`編集者`"
                :role="`editor`"
                ref="selectEditor"
              />
            </v-row>
          </v-col>
          <v-divider />
          <v-col>
            <v-card-title>記事本文</v-card-title>
            <ckeditor
              class="ma-4"
              @ready="initArticleData"
              :disabled="!editableComponent"
              :value="editorData"
              @input="updateArticle({ editorData: $event })"
              :editor="classicEditor"
              :config="editorConfig"
            ></ckeditor>
            <div align="right">
              <font color="gray">{{ editorCharacterCount }}</font>
            </div>
          </v-col>
          <template
            v-if="
              (toOriginalStatus(status) === 'unapproved' ||
                toOriginalStatus(status) === 'publish' ||
                toOriginalStatus(status) === 'unpublish') &&
              (role === 'editor' || role === 'administrator')
            "
          >
            <v-divider />
            <v-col>
              <Categories
                :readonly="!editableComponent"
                :articleId="articleId"
              />
            </v-col>
            <v-divider />
            <v-col>
              <SubCategories
                :readonly="!editableComponent || categories.length == 0"
              />
            </v-col>
            <v-divider />
            <v-col class="pa-0">
              <v-card-title>パンくず小カテゴリー設定</v-card-title>
              <v-row class="ma-4">
                <v-btn
                  :disabled="!editableComponent || categories.length == 0"
                  @click="selectMainSubCategory"
                  >選択</v-btn
                >
                <v-text-field
                  :readonly="true"
                  :value="mainSubCategory ? mainSubCategory.name : ''"
                />
                <SelectMainSubCategory ref="selectMainSubCategory" />
              </v-row>
            </v-col>
            <v-divider />
            <v-col class="pa-0">
              <v-select
                label="アナリティクスグループ"
                class="ma-4"
                :readonly="!editableComponent"
                return-object
                :value="analyticsGroup"
                @input="updateArticle({ analyticsGroup: $event })"
                item-text="name"
                item-value="id"
                :items="analyticsGroups"
              />
            </v-col>
            <v-divider />
            <v-col class="pa-0">
              <v-text-field
                class="ma-4"
                label="canonical URL"
                :readonly="!editableComponent"
                :value="canonicalUrl"
                @input="updateArticle({ canonicalUrl: $event })"
              ></v-text-field>
            </v-col>
            <v-divider />
            <v-col>
              <v-text-field
                label="RSSカテゴリー"
                :readonly="!editableComponent"
                :value="rssCategory"
                @input="updateArticle({ rssCategory: $event })"
              ></v-text-field>
            </v-col>
            <v-divider />
            <v-col class="pa-0">
              <v-card-title>広告設定</v-card-title>
              <v-checkbox
                class="ma-4"
                color="blue"
                :readonly="!editableComponent"
                :label="`広告を有効にする`"
                :input-value="isAdvertisementEnabled"
                @change="updateArticle({ isAdvertisementEnabled: $event })"
              />
              <v-select
                class="ma-4"
                label="広告の種類"
                :readonly="!editableComponent"
                return-object
                :value="inlineAdvertisementType"
                @input="
                  updateArticle({ inlineAdvertisementType: $event.value })
                "
                item-text="name"
                item-value="value"
                :items="inlineAdvertisementTypes"
              />
              <v-select
                class="ma-4"
                label="記事上バナータイプ（未設定の場合はパンくず大カテゴリの設定になります）"
                :readonly="!editableComponent"
                return-object
                :value="bannerType"
                @input="updateArticle({ bannerType: $event })"
                item-text="name"
                item-value="value"
                :items="[{ id: null, name: '', value: '' }].concat(bannerTypes)"
              />
            </v-col>
            <v-divider />
            <v-col class="pa-0">
              <v-select
                label="アプリアフィリエイト(オーバーレイ)"
                class="ma-4"
                :readonly="!editableComponent"
                return-object
                :value="overlayAppAffiliateTemplate"
                @input="updateArticle({ overlayAppAffiliateTemplate: $event })"
                item-text="name"
                item-value="id"
                :items="[{ id: null, name: '' }].concat(appAffiliateTemplates)"
              />
            </v-col>
            <v-divider />
            <v-col>
              <RelatedArticles :readonly="!editableComponent" />
            </v-col>
            <v-divider />
            <v-col class="pa-0 mx-2">
              <RssPublishDate
                :articleId="articleId"
                :rssPublishedAt="rssPublishedAt"
                @rssPublishedAt="updateRssPublishedAt"
              />
            </v-col>
            <v-divider />
            <v-col class="pa-0 mx-2 mb-4">
              <PublishDate
                :readonly="!editableComponent"
                :articleId="articleId"
                :publishedAt="publishedAt"
                :endOfPublishedAt="endOfPublishedAt"
                :status="status"
                @publishedAt="updatePublishedAt"
                @endOfPublishedAt="updateEndOfPublishedAt"
              />
            </v-col>
            <v-divider />
            <v-card-title>その他オプション</v-card-title>
            <v-row class="ma-4">
              <v-checkbox
                class="ma-0"
                color="blue"
                :readonly="!editableComponent"
                :label="`noindexを設定する`"
                :input-value="isNoIndex"
                @change="updateArticle({ isNoIndex: $event })"
                :error="isNoIndex"
                hint="※注意!! 設定するとGoogleで検索されなくなります"
                :persistent-hint="isNoIndex"
              />
              <v-spacer />
            </v-row>
            <v-row class="ma-4">
              <v-checkbox
                class="ma-0"
                color="blue"
                :readonly="!editableComponent"
                :label="`nofollowを設定する`"
                :input-value="isNoFollow"
                @change="updateArticle({ isNoFollow: $event })"
                :error="isNoFollow"
                hint="※注意!! 設定するとSEO上の影響があります"
                :persistent-hint="isNoFollow"
              />
              <v-spacer />
            </v-row>
            <v-checkbox
              class="ma-4 display-updated-at"
              color="blue"
              :readonly="!editableComponent"
              :label="`表示用更新日時を更新する`"
              :input-value="enableDisplayUpdatedAt"
              @change="updateArticle({ enableDisplayUpdatedAt: $event })"
            />
          </template>
          <v-divider />
          <v-card-actions>
            <v-btn
              color="primary"
              class="ma-1 save-button"
              :disabled="isProcess()"
              @click="onSave"
              v-if="
                editable(articleId, status, editor, writer, adminUserId, role)
              "
              >保存</v-btn
            >
            <v-spacer />
            <ChangeStatusButtons
              v-if="articleId"
              :role="role"
              :status="toOriginalStatus(status)"
              :statusName="status"
              @emitStatus="updateStatus"
              @emitErrorMessages="updateAlertMessagesFromChangeStatus"
            />
          </v-card-actions>
        </v-card>
      </v-form>
    </v-col>
    <v-col class="pa-0">
      <ErrorMessages
        v-if="alertMessages.length > 0"
        :messages="alertMessages"
      />
    </v-col>

    <!-- CKEditorデバッグ領域 -->
    <!-- <div>
      <p id="editor"></p>
    </div> -->
    <!-- CKEditorデバッグ領域 -->
  </div>
</template>
<script>
//TypeScriptで記述されていない為、ESLintの以下のルールを無効にしている
/*eslint @typescript-eslint/explicit-module-boundary-types: "off"*/
import ClassicEditor from "@ckeditor/ckeditor5-editor-classic/src/classiceditor"
import Alignment from "@ckeditor/ckeditor5-alignment/src/alignment"
import Essentials from "@ckeditor/ckeditor5-essentials/src/essentials"
import Bold from "@ckeditor/ckeditor5-basic-styles/src/bold"
import Italic from "@ckeditor/ckeditor5-basic-styles/src/italic"
import Underline from "@ckeditor/ckeditor5-basic-styles/src/underline"
import Link from "@ckeditor/ckeditor5-link/src/link"
import Paragraph from "@ckeditor/ckeditor5-paragraph/src/paragraph"
import Heading from "@ckeditor/ckeditor5-heading/src/heading"
import List from "@ckeditor/ckeditor5-list/src/list"
import Image from "@ckeditor/ckeditor5-image/src/image"
import Font from "@ckeditor/ckeditor5-font/src/font"
import ImageCaption from "@ckeditor/ckeditor5-image/src/imagecaption"
import ImageResize from "@ckeditor/ckeditor5-image/src/imageresize"
import BlockQuote from "@ckeditor/ckeditor5-block-quote/src/blockquote"
import WordCount from "@ckeditor/ckeditor5-word-count/src/wordcount"
import MediaEmbed from "@ckeditor/ckeditor5-media-embed/src/mediaembed"
import HorizontalLine from "@ckeditor/ckeditor5-horizontal-line/src/horizontalline"

// plugins
import RecommendBox from "../plugins/recommend-box/recommendBox"
import TableOfContentsBox from "../plugins/table-of-content-box/tableOfContentsBox"
import NextPageBox from "../plugins/nextpagebox/nextPageBox"
import InsertImage from "../plugins/image/insertImage"
import InsertImageWithoutIsbn from "../plugins/imagewithoutisbn/insertimagewithoutisbn"
import AppAffiliateTemplate from "../plugins/appaffiliatetemplate/appaffiliatetemplate"
import InlineAdvertisement from "../plugins/inlineadvertisement/inlineadvertisement"
import UnderlineColor from "@/plugins/underlinecolor/underlinecolor"
import ImageLazyLoading from "@/plugins/imagelazyloading/imagelazyloading"
import ArticleLinkBox from "../plugins/articlelinkbox/articleLinkBox"

// api
import api from "../honcierge-api"

// for debug
// import CKEditorInspector from '@ckeditor/ckeditor5-inspector';

// components
import ImageCrop from "@/components/ImageCrop"
import RelatedArticles from "../components/RelatedArticles"
import Categories from "@/components/Categories"
import PublishDate from "@/components/PublishDate"
import RssPublishDate from "@/components/RssPublishDate"
import ChangeStatusButtons from "@/components/ChangeStatusButtons"
import ErrorMessages from "@/components/ErrorMessages"
import SelectAdminUser from "@/components/Article/SelectAdminUser"
import SelectCurator from "@/components/Article/SelectCurator"
import SelectMainSubCategory from "@/components/Article/SelectMainSubCategory"
import SubCategories from "@/components/Article/SubCategories"
import Loading from "@/components/Loading"

// mixins
import EditableMixin from "../mixins/editable-mixin"
import StatusMixin from "../mixins/status-mixin"
import ProcessingMixin from "../mixins/processing-mixin"

import { mapState, mapActions, mapMutations } from "vuex"

export default {
  mixins: [EditableMixin, StatusMixin, ProcessingMixin],
  name: "app",
  props: {
    articleId: Number,
  },
  components: {
    Loading,
    ImageCrop,
    RelatedArticles,
    Categories,
    PublishDate,
    RssPublishDate,
    ChangeStatusButtons,
    ErrorMessages,
    SelectAdminUser,
    SelectCurator,
    SelectMainSubCategory,
    SubCategories,
  },
  computed: {
    ...mapState("article", {
      title: (state) => state.title,
      rssTitle: (state) => state.rssTitle,
      rssCategory: (state) => state.rssCategory,
      overview: (state) => state.overview,
      eyecatch: (state) => state.eyecatch,
      thumbnail: (state) => state.thumbnail,
      writer: (state) => state.writer,
      editor: (state) => state.editor,
      curator: (state) => state.curator,
      articleType: (state) => state.articleType,
      editorData: (state) => state.editorData,
      relatedArticles: (state) => state.relatedArticles,
      categories: (state) => state.categories,
      status: (state) => state.status,
      writerDisplayEnabled: (state) => state.writerDisplayEnabled,
      isAdvertisementEnabled: (state) => state.isAdvertisementEnabled,
      inlineAdvertisementType: (state) => state.inlineAdvertisementType,
      isNoIndex: (state) => state.isNoIndex,
      isNoFollow: (state) => state.isNoFollow,
      showRss: (state) => state.showRss,
      canonicalUrl: (state) => state.canonicalUrl,
      publishedAt: (state) => state.publishedAt,
      rssPublishedAt: (state) => state.rssPublishedAt,
      endOfPublishedAt: (state) => state.endOfPublishedAt,
      analyticsGroup: (state) => state.analyticsGroup,
      overlayAppAffiliateTemplate: (state) => state.overlayAppAffiliateTemplate,
      enableDisplayUpdatedAt: (state) => state.enableDisplayUpdatedAt,
      mainSubCategory: (state) => state.mainSubCategory,
      bannerType: (state) => state.bannerType,
    }),
    ...mapState("adminUser", { adminUserId: "id", role: "role" }),
    ...mapState("master", {
      appAffiliateTemplates: (state) => state.appAffiliateTemplates,
    }),
    editableComponent() {
      return this.editable(
        this.articleId,
        this.status,
        this.editor,
        this.writer,
        this.adminUserId,
        this.role
      )
    },
    isWellKnown() {
      return this.articleType && this.articleType.value === "wellKnown"
    },
  },
  methods: {
    ...mapActions("article", ["updateArticle"]),
    ...mapMutations("article", ["initArticle"]),
    ...mapMutations("master", ["initMaster"]),
    updatePublishedAt(datetime) {
      this.updateArticle({ publishedAt: datetime })
    },
    updateEndOfPublishedAt(datetime) {
      this.updateArticle({ endOfPublishedAt: datetime })
    },
    updateRssPublishedAt(datetime) {
      this.updateArticle({ rssPublishedAt: datetime })
    },
    updateStatus(status) {
      this.updateArticle({ status })
    },
    updateArticleType(articleType) {
      this.updateArticle({ articleType: articleType })
      if (event.value !== "wellKnown") {
        this.updateArticle({ curator: null })
      }
    },
    async initArticleData() {
      let promises = []
      if (this.articleId) {
        promises.push(this.getArticle())
      }
      promises.push(this.getArticleTypes())
      promises.push(this.getBannerTypes())
      if (this.role === "editor" || this.role === "administrator") {
        promises.push(this.getAnalyticsGroupMaster())
      }
      await Promise.all(promises)
      this.isLoading = false
    },
    onSave() {
      if (this.$refs.form.validate()) {
        this.startProcess()
        const DEFAULT_ERROR_MESSAGE = ["保存に失敗しました"]
        if (this.articleId) {
          api
            .updateArticle()
            .then(() => {
              this.alertMessages = []
              this.$toasted.success("更新しました")
            })
            .catch((e) => {
              const messages = e.response.data.messages
              this.alertMessages =
                messages.length > 0 ? messages : DEFAULT_ERROR_MESSAGE
            })
            .finally(() => {
              this.endProcess()
            })
        } else {
          api
            .createArticle()
            .then(() => {
              this.$toasted.success("新規作成しました")
              this.$router.push({ path: "/articles" })
            })
            .catch((e) => {
              const messages = e.response.data.messages
              this.alertMessages =
                messages.length > 0 ? messages : DEFAULT_ERROR_MESSAGE
            })
            .finally(() => {
              this.endProcess()
            })
        }
      }
    },
    selectWriter() {
      this.$refs.selectWriter.dialog = true
      this.$refs.selectWriter.clear()
    },
    selectEditor() {
      this.$refs.selectEditor.dialog = true
      this.$refs.selectEditor.clear()
    },
    selectCurator() {
      this.$refs.selectCurator.dialog = true
      this.$refs.selectCurator.clear()
    },
    selectMainSubCategory() {
      this.$refs.selectMainSubCategory.dialog = true
      this.$refs.selectMainSubCategory.clear()
      this.$refs.selectMainSubCategory.onChangePage()
    },
    getArticle() {
      return api
        .getArticle(this.articleId)
        .then((response) => {
          const article = response.data.article
          this.updateArticle(article)
        })
        .catch(() => {
          this.$router.push({ path: "/articles" })
          this.$toasted.error("記事の取得に失敗しました")
        })
    },
    getArticleTypes() {
      return api
        .getArticleTypes()
        .then((response) => {
          this.articleTypes = response.data
        })
        .catch(() => {
          this.$toasted.error("記事タイプのマスター取得に失敗しました")
        })
    },
    getBannerTypes() {
      return api
        .getBannerTypes()
        .then((response) => {
          this.bannerTypes = response.data
        })
        .catch(() => {
          this.$toasted.error("バナータイプのマスター取得に失敗しました")
        })
    },
    getAnalyticsGroupMaster() {
      return api
        .getAnalyticsGroups()
        .then((response) => {
          this.analyticsGroups = response.data.analyticsGroups
          this.analyticsGroups.unshift({ id: null, name: "" })
        })
        .catch(() => {
          this.$toasted.error(
            "アナリティクスグループのマスター取得に失敗しました"
          )
        })
    },
    getCuratorMaster() {
      return api
        .getCurators({ page: 1, per: 10000000, sortBy: "id", descending: true })
        .then((response) => {
          this.curators = response.data.curators
        })
        .catch(() => {
          this.$toasted.error("著名人情報のマスター取得に失敗しました")
        })
    },
    showEyecatchCrop() {
      this.$refs.eyecatchCrop.dialog = true
      this.$refs.eyecatchCrop.showFileChooser()
    },
    showThumbnailCrop() {
      this.$refs.thumbnailCrop.dialog = true
      this.$refs.thumbnailCrop.showFileChooser()
    },
    updateAlertMessagesFromChangeStatus(errorMessages) {
      this.alertMessages = errorMessages
    },
    makeToolbar() {
      const ret = [
        "alignment",
        "bold",
        "italic",
        "underline",
        "link",
        "horizontalLine",
        "blockQuote",
        "undo",
        "redo",
        "|",
        "fontColor",
        "fontBackgroundColor",
        "underlineColor",
        "|",
        "heading",
        "tableOfContentsBox",
        "|",
        "numberedList",
        "bulletedList",
        "insertImage",
        "mediaEmbed",
        "recommendBox",
      ]
      const role = this.$store.state.adminUser.role
      if (role === "editor" || role === "administrator") {
        ret.push("insertImageWithoutIsbn")
      }
      ret.push(...["nextPageBox", "articleLinkBox"])

      if (role === "editor" || role === "administrator") {
        ret.push("appAffiliateTemplate")
        ret.push("inlineAdvertisement")
      }
      return ret
    },
  },
  data() {
    return {
      isLoading: true,
      alertMessages: [],
      articleTypes: [],
      bannerTypes: [],
      curators: [],
      writers: [],
      editors: [],
      analyticsGroups: [],
      inlineAdvertisementTypes: [
        { id: 0, name: "通常", value: "on" },
        { id: 1, name: "Googleなし", value: "exclude_google" },
      ],
      loadedEyecatchImage: "",
      loadedEyecatchImageName: "",
      classicEditor: ClassicEditor,
      editorCharacterCount: 0,
      editorConfig: {
        plugins: [
          Alignment,
          Essentials,
          Bold,
          Italic,
          Underline,
          Link,
          Paragraph,
          Heading,
          List,
          RecommendBox,
          InsertImageWithoutIsbn,
          TableOfContentsBox,
          NextPageBox,
          InsertImage,
          Image,
          ImageCaption,
          ImageResize,
          Font,
          BlockQuote,
          HorizontalLine,
          WordCount,
          AppAffiliateTemplate,
          InlineAdvertisement,
          UnderlineColor,
          MediaEmbed,
          ImageLazyLoading,
          ArticleLinkBox,
        ],
        heading: {
          options: [
            {
              model: "paragraph",
              title: "本文",
              class: "ck-heading_paragraph",
            },
            {
              model: "heading1",
              view: "h2",
              title: "見出しh2",
              class: "ck-heading_heading1",
            },
            {
              model: "heading2",
              view: "h3",
              title: "見出しh3",
              class: "ck-heading_heading2",
            },
            {
              model: "heading3",
              view: "h4",
              title: "見出しh4",
              class: "ck-heading_heading3",
            },
          ],
        },
        image: {
          resizeUnit: "px",
        },
        wordCount: {
          onUpdate: (stats) => {
            this.editorCharacterCount = stats.characters
          },
        },
        mediaEmbed: {
          providers: [
            {
              name: "youtube",
              url: [
                /^(?:m\.)?youtube\.com\/watch\?v=([\w-]+)/,
                /^(?:m\.)?youtube\.com\/v\/([\w-]+)/,
                /^youtube\.com\/embed\/([\w-]+)/,
                /^youtu\.be\/([\w-]+)/,
              ],
              html: (match) => {
                const id = match[1]

                return (
                  '<div style="position: relative; padding-bottom: 100%; height: 0; padding-bottom: 56.2493%;">' +
                  `<iframe src="https://www.youtube.com/embed/${id}" ` +
                  'style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;" ' +
                  'frameborder="0" allow="autoplay; encrypted-media" allowfullscreen>' +
                  "</iframe>" +
                  "</div>"
                )
              },
            },
            {
              name: "instagram",
              url: /^instagram\.com\/p\/(\w+)/,
            },
            {
              name: "twitter",
              url: /^twitter\.com/,
            },
          ],
        },
        toolbar: this.makeToolbar(),
      },
    }
  },
  created() {
    this.initArticle()
    this.initMaster()
  },
  mounted() {
    // this.classicEditor.create(document.querySelector("#editor"), {
    //   plugins: this.editorConfig.plugins,
    //   toolbar: this.editorConfig.toolbar,
    //   placeholder: 'CKEditorデバッグ用領域',
    // }).then((editor) => {
    //     CKEditorInspector.attach(editor);
    //   }
    // ).catch((error) => {
    //   console.log(error.stack);
    // });
  },
}
</script>

<style lang="scss">
article.container {
  padding: 0px;
}
.recommend-box,
.table-of-contents-box,
.next-page-box,
.article-link-box {
  padding: 10px;
  margin: 1em 0;

  background: rgba(0, 0, 0, 0.1);
  border: solid 1px hsl(0, 0%, 77%);
  border-radius: 2px;
}
.app-affiliate-template-box {
  padding: 10px;
  margin: 1em 0;

  background: #45cf83;
  border: solid 1px hsl(0, 0%, 77%);
  border-radius: 2px;
}
.inline-advertisement-box {
  padding: 10px;
  margin: 1em 0;

  background: #ccffff;
  border: solid 1px hsl(0, 0%, 77%);
  border-radius: 2px;
}
.next-page-box,
.recommend-box,
.article-link-box {
  display: flex;
  align-items: baseline;
  padding: 10px;
  margin: 1em 0;

  background: #ccffff;
  border: solid 1px hsl(0, 0%, 77%);
  border-radius: 2px;
}
.next-page-box-label,
.recommend-box-label,
.article-link-box-label {
  margin-right: 10px;
}
.next-page-box-title,
.recommend-box-isbn,
.article-link-box-id {
  display: inline;
}
.recommend-box-error-label {
  padding-right: 10px;
}
.recommend-box-error-label-disable {
  visibility: hidden;
  width: 0px;
  height: 0px;
}
label.recommend-box-error-label {
  color: crimson;
}
.recommend-box-children {
  display: inline-flex;
}
.table-of-contents-box-title,
.next-page-box-title,
.recommend-box-isbn,
.article-link-box-id {
  padding: 10px;
  margin: 0;

  background: #fff;
  border: solid 1px hsl(0, 0%, 77%);
}
.article-link-box-excuse {
  display: inline;
}
.ck-content {
  text-align: left;
}
ul {
  list-style-type: disc;
}
.eyecatch-image {
  width: 200px;
  height: auto;
}
.thumbnail-image {
  width: 75px;
  height: auto;
}
</style>
